// Lógica pura de los "Avisos operativos" de la IA del helpdesk (sin React ni
// i18n): normaliza el contrato del BFF, clasifica por vigencia y convierte
// entre la hora de pared de Chile (formato de <input type="datetime-local">) y
// el instante ISO que viaja al backend.
//
// El backend todavía se está construyendo: TODA lectura de nombres de campos de
// respuesta vive acá. El estado (vigente/próximo/vencido) se calcula SIEMPRE en
// el cliente comparando instantes; un `status` del backend se ignora.
//
// Los mensajes de validación son claves en español: la vista los traduce con
// t(...) al pintarlos.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const NOTICES_TZ = "America/Santiago";
export const NOTICE_TEXT_MAX = 500;
// Tope de duración de un aviso: más largo que eso deja de ser "operativo"
// (o el admin está cargando una regla fija, que no es este módulo).
export const NOTICE_MAX_DAYS = 31;
// El GET ya trae los vencidos de la última semana; se recorta acá también por
// si el backend todavía no lo hace exacto.
export const NOTICE_EXPIRED_WINDOW_DAYS = 7;

// Orden de presentación: Comercios, Clientes, Repartidores, Todos.
export const NOTICE_AUDIENCES = ["merchant", "customer", "driver", "all"];
export const NOTICE_VIEWS = ["active", "upcoming", "expired"];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// El backend contrata instantes ISO 8601 con Z u offset; Date.parse los lee sin
// depender de la zona del navegador.
const text = (value) => (typeof value === "string" ? value : "");

const textOrNull = (value) => (typeof value === "string" ? value : null);

function toInstant(value) {
  if (typeof value !== "string" || !value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** Audiencias válidas, sin duplicados, en el orden en que llegaron. */
function normalizeAudiences(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw.filter((id) => {
    if (!NOTICE_AUDIENCES.includes(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * Normaliza un Notice del contrato. Devuelve null sin id (no se podría editar
 * ni borrar) y también sin vigencia: una fila cuyas fechas no forman instantes
 * válidos no se puede clasificar, así que no se pinta.
 * @param {unknown} raw
 * @returns {{id: string, text: string, startsAt: string|null, endsAt: string|null, startMs: number|null, endMs: number|null, audiences: string[], noOperation: boolean, createdAt: unknown, updatedAt: unknown}|null}
 */
export function normalizeNotice(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id || raw._id;
  if (!id) return null;

  const startsAt = textOrNull(raw.starts_at);
  const endsAt = textOrNull(raw.ends_at);
  const startMs = toInstant(startsAt);
  const endMs = toInstant(endsAt);
  if (startMs === null || endMs === null) return null;

  return {
    id: String(id),
    text: text(raw.text),
    // Se conserva el ISO tal cual: es el valor que vuelve al backend sin
    // reserializar ni perder el offset original.
    startsAt,
    endsAt,
    startMs,
    endMs,
    audiences: normalizeAudiences(raw.audiences),
    noOperation: raw.no_operation === true,
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

/**
 * Respuesta de la lista. Acepta `notices` o el alias `items` (backend en
 * transición) y también la variante agrupada (active/upcoming/expired); en ese
 * caso los grupos se concatenan y el orden lo vuelve a poner groupNotices.
 * @param {unknown} res
 * @returns {object[]}
 */
export function normalizeNoticesResponse(res) {
  const payload = res && typeof res === "object" ? res : {};

  let list = [];
  if (Array.isArray(payload.notices)) {
    list = payload.notices;
  } else if (Array.isArray(payload.items)) {
    list = payload.items;
  } else if (NOTICE_VIEWS.some((view) => Array.isArray(payload[view]))) {
    list = NOTICE_VIEWS.reduce(
      (acc, view) => (Array.isArray(payload[view]) ? acc.concat(payload[view]) : acc),
      []
    );
  }

  return list.map(normalizeNotice).filter(Boolean);
}

/**
 * Respuesta de una mutación (`{ ok, notice }`) o el notice suelto.
 * @param {unknown} res
 * @returns {object|null}
 */
export function normalizeNoticeResponse(res) {
  if (!res || typeof res !== "object") return null;
  return normalizeNotice(res.notice ?? res);
}

/**
 * Estado de un aviso normalizado: vigente mientras [inicio, fin), próximo
 * antes del inicio y vencido desde el fin inclusive.
 * @param {{startMs: number, endMs: number}} notice
 * @param {number} nowMs
 * @returns {"active"|"upcoming"|"expired"}
 */
export function classifyNotice(notice, nowMs) {
  if (notice.startMs <= nowMs && nowMs < notice.endMs) return "active";
  if (nowMs < notice.startMs) return "upcoming";
  return "expired";
}

/**
 * Agrupa para la vista. Orden dentro de cada grupo: el vigente que vence antes
 * primero, el próximo que empieza antes primero y el vencido más reciente
 * primero. Los vencidos de hace más de NOTICE_EXPIRED_WINDOW_DAYS se descartan
 * (solo ensucian la lista y ya no le importan a nadie).
 * @param {object[]} notices
 * @param {number} nowMs
 * @returns {{active: object[], upcoming: object[], expired: object[]}}
 */
export function groupNotices(notices, nowMs) {
  const groups = { active: [], upcoming: [], expired: [] };
  if (!Array.isArray(notices)) return groups;

  notices.forEach((notice) => {
    if (!notice) return;
    const view = classifyNotice(notice, nowMs);
    if (view === "expired" && nowMs - notice.endMs > NOTICE_EXPIRED_WINDOW_DAYS * MS_PER_DAY) {
      return;
    }
    groups[view].push(notice);
  });

  groups.active.sort((a, b) => a.endMs - b.endMs);
  groups.upcoming.sort((a, b) => a.startMs - b.startMs);
  groups.expired.sort((a, b) => b.endMs - a.endMs);
  return groups;
}

/**
 * Instante ISO → valor de <input type="datetime-local"> con la hora de pared de
 * Chile. "" cuando no hay instante válido (el input muestra vacío).
 * @param {unknown} iso
 * @returns {string}
 */
export function isoToChileInput(iso) {
  if (typeof iso !== "string" || !iso.trim()) return "";
  const parsed = dayjs(iso);
  if (!parsed.isValid()) return "";
  return parsed.tz(NOTICES_TZ).format("YYYY-MM-DDTHH:mm");
}

const CHILE_INPUT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * Valor de <input type="datetime-local"> (hora de pared de Chile) → instante
 * ISO UTC. null si el valor no existe: formato distinto, fecha fuera de rango
 * o una hora que dayjs normalizaría (30 de febrero, 25:00).
 * @param {unknown} value
 * @returns {string|null}
 */
export function chileInputToIso(value) {
  if (typeof value !== "string" || !CHILE_INPUT_RE.test(value)) return null;

  const parsed = dayjs.tz(value, "YYYY-MM-DDTHH:mm", NOTICES_TZ);
  if (!parsed.isValid()) return null;

  const iso = parsed.toISOString();
  if (!iso) return null;
  // dayjs resuelve fechas desbordadas (30 de febrero → marzo): si la hora de
  // pared no vuelve idéntica, el valor no existe y no se acepta.
  if (isoToChileInput(iso) !== value) return null;

  return iso;
}

/**
 * Ventana por defecto del formulario: desde la hora en punto actual de Chile
 * hasta el final de ese mismo día. Cubre el caso típico ("hoy pasa esto").
 * @param {number} nowMs
 * @returns {{startsAt: string, endsAt: string}}
 */
export function defaultNoticeWindow(nowMs) {
  const now = dayjs(nowMs).tz(NOTICES_TZ);
  return {
    startsAt: now.startOf("hour").format("YYYY-MM-DDTHH:mm"),
    endsAt: now.endOf("day").format("YYYY-MM-DDTHH:mm"),
  };
}

// Un valor con el formato correcto que no da instante es una fecha imposible
// o la hora que se salta el cambio al horario de verano (en Chile, 00:00–00:59
// del primer domingo de septiembre): decirlo evita que el admin crea que el
// campo quedó vacío.
const NONEXISTENT_TIME_ERROR = "Esa fecha u hora no existe en Chile (cambio de horario).";

function missingInstantError(value, emptyMessage) {
  return typeof value === "string" && CHILE_INPUT_RE.test(value)
    ? NONEXISTENT_TIME_ERROR
    : emptyMessage;
}

/**
 * Errores del borrador, por campo. `{}` significa que se puede enviar.
 * startsAt/endsAt llegan como valores de datetime-local (hora de Chile).
 * Los límites de texto se miden sobre el texto ya recortado: los espacios que
 * el backend tampoco guarda no deberían hacer fallar el formulario.
 * @param {{text?: string, startsAt?: string, endsAt?: string, audiences?: string[]}} draft
 * @returns {{text?: string, startsAt?: string, endsAt?: string, audiences?: string}}
 */
export function validateNoticeDraft({ text: rawText, startsAt, endsAt, audiences } = {}) {
  const errors = {};
  const normalizedText = (typeof rawText === "string" ? rawText : "").trim();

  if (!normalizedText) {
    errors.text = "Escribe el aviso.";
  } else if (normalizedText.length > NOTICE_TEXT_MAX) {
    errors.text = `El aviso admite hasta ${NOTICE_TEXT_MAX} caracteres.`;
  }

  const startIso = chileInputToIso(startsAt);
  const endIso = chileInputToIso(endsAt);

  if (!startIso) {
    errors.startsAt = missingInstantError(startsAt, "Indica desde cuándo rige.");
  }
  if (!endIso) {
    errors.endsAt = missingInstantError(endsAt, "Indica hasta cuándo rige.");
  }
  if (startIso && endIso) {
    const startMs = Date.parse(startIso);
    const endMs = Date.parse(endIso);
    if (endMs <= startMs) {
      errors.endsAt = "El término debe ser posterior al inicio.";
    } else if (endMs - startMs > NOTICE_MAX_DAYS * MS_PER_DAY) {
      errors.endsAt = `Un aviso puede durar como máximo ${NOTICE_MAX_DAYS} días.`;
    }
  }

  if (normalizeAudiences(audiences).length === 0) {
    errors.audiences = "Elige al menos una audiencia.";
  }

  return errors;
}

/**
 * Body que viaja al backend (POST y PATCH). Texto recortado, instantes UTC y
 * "all" exclusivo: con "all" las demás audiencias no aportan nada y solo
 * ensucian el aviso guardado.
 * @param {{text?: string, startsAt?: string, endsAt?: string, audiences?: string[], noOperation?: boolean}} draft
 * @returns {{text: string, starts_at: string|null, ends_at: string|null, audiences: string[], no_operation: boolean}}
 */
export function draftToBody(draft = {}) {
  const audiences = normalizeAudiences(draft.audiences);
  return {
    text: text(draft.text).trim(),
    starts_at: chileInputToIso(draft.startsAt),
    ends_at: chileInputToIso(draft.endsAt),
    audiences: audiences.includes("all") ? ["all"] : audiences,
    no_operation: draft.noOperation === true,
  };
}

/**
 * true cuando crear, editar o borrar el aviso exige el tier de administrador:
 * "sin operación" para TODAS las audiencias apaga la operación del courier
 * entero frente a cualquier contacto (requiredWriteAction del backend, que
 * revalida cada escritura). Acepta un aviso normalizado o un borrador: ambos
 * traen `noOperation` y `audiences`.
 * @param {{noOperation?: boolean, audiences?: string[]}} noticeOrDraft
 * @returns {boolean}
 */
export function requiresAdmin(noticeOrDraft) {
  return (
    noticeOrDraft?.noOperation === true &&
    Array.isArray(noticeOrDraft?.audiences) &&
    noticeOrDraft.audiences.includes("all")
  );
}

/**
 * Body del PATCH de edición: SOLO los campos que cambiaron (`{}` = nada que
 * guardar; el backend responde 400 a un PATCH vacío).
 *
 * Las fechas se comparan como hora de pared de Chile, tal como las ve el
 * formulario, y viajan solo si el admin las tocó: reenviar las intactas las
 * haría pasar por la conversión hora de Chile → instante, que pierde los
 * segundos y, en la hora que se repite al volver al horario de invierno
 * (23:00–23:59 del sábado de abril), elige la primera pasada. Un aviso guardado
 * en la segunda se correría una hora con solo cambiarle el texto. El backend
 * acepta el PATCH parcial y valida la vigencia contra el aviso combinado.
 * @param {object} notice aviso normalizado (el original)
 * @param {object} draft borrador del formulario
 * @returns {{text?: string, starts_at?: string|null, ends_at?: string|null, audiences?: string[], no_operation?: boolean}}
 */
export function noticeEditChanges(notice, draft) {
  const original = noticeToDraft(notice);
  const before = draftToBody(original);
  const after = draftToBody(draft);
  const changes = {};
  if (after.text !== text(notice?.text).trim()) changes.text = after.text;
  if ((draft?.startsAt || "") !== original.startsAt) changes.starts_at = after.starts_at;
  if ((draft?.endsAt || "") !== original.endsAt) changes.ends_at = after.ends_at;
  if ([...before.audiences].sort().join(",") !== [...after.audiences].sort().join(",")) {
    changes.audiences = after.audiences;
  }
  if (after.no_operation !== before.no_operation) changes.no_operation = after.no_operation;
  return changes;
}

/**
 * Aviso normalizado → borrador del formulario de edición.
 * @param {object} notice
 * @returns {{text: string, startsAt: string, endsAt: string, audiences: string[], noOperation: boolean}}
 */
export function noticeToDraft(notice) {
  const source = notice && typeof notice === "object" ? notice : {};
  return {
    text: text(source.text),
    startsAt: isoToChileInput(source.startsAt),
    endsAt: isoToChileInput(source.endsAt),
    audiences: normalizeAudiences(source.audiences),
    noOperation: source.noOperation === true,
  };
}

/** Formatter de Intl con la zona de Chile; un locale inválido cae a "es". */
function makeChileFormatter(locale, options) {
  try {
    return new Intl.DateTimeFormat(locale || "es", { timeZone: NOTICES_TZ, ...options });
  } catch {
    return new Intl.DateTimeFormat("es", { timeZone: NOTICES_TZ, ...options });
  }
}

/**
 * Rango legible en hora de Chile. Si inicio y fin caen el mismo día, la fecha
 * se pinta una sola vez y el fin queda como hora suelta: "lun, 2 nov, 00:00 –
 * 23:59". Con días distintos se repite la fecha completa en ambos extremos.
 * @param {{startsAt?: string, endsAt?: string}} notice aviso normalizado
 * @param {string} [locale]
 * @returns {string}
 */
export function formatNoticeRange(notice, locale) {
  const startIso = notice?.startsAt;
  const endIso = notice?.endsAt;
  // dayjs(undefined) es "ahora": sin ambos instantes no hay rango que pintar.
  if (typeof startIso !== "string" || typeof endIso !== "string") return "";

  const start = dayjs(startIso);
  const end = dayjs(endIso);
  if (!start.isValid() || !end.isValid()) return "";

  const sameDay =
    start.tz(NOTICES_TZ).format("YYYY-MM-DD") === end.tz(NOTICES_TZ).format("YYYY-MM-DD");

  const dateTime = makeChileFormatter(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const timeOnly = makeChileFormatter(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

  const startLabel = dateTime.format(start.toDate());
  const endLabel = sameDay ? timeOnly.format(end.toDate()) : dateTime.format(end.toDate());
  return `${startLabel} – ${endLabel}`;
}
